import { CbhIcon, Text, Title } from "@clipboard-health/ui-components";
import { alpha, Box, ButtonBase, DialogContent, Stack } from "@mui/material";

import { formatRecordingTime } from "../utils/formatRecordingTime";
import { RecordButton } from "./RecordButton";

export interface VideoRecorderScreenProps {
  videoRef: (element: HTMLVideoElement | null) => void;
  isReady: boolean;
  isRecording: boolean;
  recordingTime: number;
  isErrored?: boolean;
  errorMessage?: string;
  onStartRecording: () => void;
  onStopRecording: () => void;
  onFlipCamera: () => void;
}

export function VideoRecorderScreen(props: VideoRecorderScreenProps) {
  const {
    videoRef,
    isReady,
    isRecording,
    recordingTime,
    isErrored,
    errorMessage,
    onStartRecording,
    onStopRecording,
    onFlipCamera,
  } = props;

  return (
    <DialogContent
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        flex: 1,
      }}
    >
      <Stack spacing={4} alignItems="center" sx={{ width: "100%" }}>
        <Title variant="h3" component="h3" textAlign="center">
          {isRecording ? "Recording..." : "Record a video"}
        </Title>
        <Text variant="body2" color="text.secondary" textAlign="center">
          {isRecording ? "Tap stop when you're done." : "Tap record and speak your experience."}
        </Text>

        <Box
          sx={(theme) => ({
            width: "100%",
            aspectRatio: "4 / 3",
            borderRadius: theme.spacing(3),
            overflow: "hidden",
            backgroundColor: theme.palette.common.black,
            position: "relative",
          })}
        >
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              transform: "scaleX(-1)",
            }}
          />
          {!isReady && !isErrored && !isRecording && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CbhIcon
                type="video-outlined"
                size="large"
                color={(theme) => alpha(theme.palette.common.white, 0.5)}
              />
            </Box>
          )}
          {!isRecording && (
            <ButtonBase
              sx={(theme) => ({
                position: "absolute",
                top: theme.spacing(2),
                right: theme.spacing(2),
                width: 36,
                height: 36,
                borderRadius: "50%",
                backgroundColor: alpha(theme.palette.common.black, 0.5),
              })}
              onClick={onFlipCamera}
            >
              <CbhIcon
                type="swap-horizontal"
                size="small"
                color={(theme) => theme.palette.common.white}
              />
            </ButtonBase>
          )}
          {isRecording && (
            <Box
              sx={(theme) => ({
                position: "absolute",
                top: theme.spacing(2),
                left: theme.spacing(2),
                display: "flex",
                alignItems: "center",
                gap: theme.spacing(1),
              })}
            >
              <Box
                sx={(theme) => ({
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: theme.palette.error.main,
                })}
              />
              <Text variant="body2" sx={(theme) => ({ color: theme.palette.common.white })}>
                {formatRecordingTime(recordingTime)}
              </Text>
            </Box>
          )}
        </Box>

        {isErrored && errorMessage && (
          <Text variant="body2" color="error.main" textAlign="center">
            {errorMessage}
          </Text>
        )}

        <RecordButton
          disabled={!isReady && !isRecording}
          isRecording={isRecording}
          onClick={isRecording ? onStopRecording : onStartRecording}
        />
      </Stack>
    </DialogContent>
  );
}
